import { useEffect, useRef, useCallback } from 'react'

import { useLevelContext } from '@/features/level-play'
import { normalize } from '@/shared/lib/analytics/normalize'
import {
  shouldSendOnce,
  throttleOncePer,
  bucketize
} from '@/shared/lib/analytics/shouldSendOnce'
import { LEVEL_SCREENS } from '@/shared/lib/analytics/types'
import { useAnalytics } from '@/shared/lib/analytics/useAnalytics'
import { useFlow } from '@/shared/lib/steps-flow'
import { STEP_KIND } from '@/shared/lib/steps-flow/types'


export function useLevelAnalytics() {
  const { track } = useAnalytics()
  const { level, challenges } = useLevelContext()
  const { state } = useFlow()

  const levelStateRef = useRef<{
    viewed: boolean
    started: boolean
    startTime: number
    challengesCompleted: number
    totalChallenges: number
  }>({
    viewed: false,
    started: false,
    startTime: 0,
    challengesCompleted: 0,
    totalChallenges: challenges?.length || 0
  })

  const currentChallengeRef = useRef<{
    id: number
    type: string
    startTime: number
  } | null>(null)

  const trackLevelViewed = useCallback(() => {
    if (!level) return

    const shouldTrack = shouldSendOnce('level_viewed', level.id, 30 * 60 * 1000)
    if (!shouldTrack) return

    levelStateRef.current.viewed = true

    track.event('level_viewed', {
      ...normalize.level(level),
      total_challenges: challenges?.length || 0,
      max_lives: state.maxLives,
      context_route: window.location.pathname,
      context_screen: LEVEL_SCREENS.INITIAL_SCREEN
    })
  }, [track, level, challenges, state.maxLives])

  const trackLevelStartedOrResumed = useCallback(
    (reason: 'fresh' | 'resume' = 'fresh') => {
      if (!level) return

      const throttleKey = `level_start:${level.id}:${reason}`
      if (!throttleOncePer(throttleKey, 2000)) return

      levelStateRef.current.started = true
      levelStateRef.current.startTime = Date.now()

      track.level.startedOrResumed({
        ...normalize.level(level),
        total_challenges: challenges?.length || 0,
        start_time: levelStateRef.current.startTime,
        reason,
        context_screen: LEVEL_SCREENS.STEPS_SCREEN,
        context_route: window.location.pathname
      })
    },
    [track, level, challenges]
  )

  const trackLevelLeave = useCallback(
    (explicitAction: boolean = false) => {
      if (!explicitAction || !level || !levelStateRef.current.started) return

      const completionTime = Date.now() - levelStateRef.current.startTime
      const progressPercentage =
        levelStateRef.current.totalChallenges > 0
          ? (levelStateRef.current.challengesCompleted /
              levelStateRef.current.totalChallenges) *
            100
          : 0

      track.event('level_leave', {
        ...normalize.level(level),
        completion_time: completionTime,
        completion_time_bucket: bucketize(
          completionTime / 1000,
          [0, 15, 30, 60, 120]
        ),
        progress_percentage: progressPercentage,
        challenges_completed: levelStateRef.current.challengesCompleted,
        total_challenges: levelStateRef.current.totalChallenges,
        lives_remaining: state.lives,
        context_screen: LEVEL_SCREENS.STEPS_SCREEN
      })
    },
    [track, level, state.lives]
  )

  const trackLevelFinished = useCallback(() => {
    if (!level || !levelStateRef.current.started) return

    const completionTime = Date.now() - levelStateRef.current.startTime

    track.event('level_finished', {
      level_id: level.id,
      level_uid: level.uid,
      level_name: level.name,
      completion_time: completionTime,
      challenges_completed: levelStateRef.current.totalChallenges,
      total_challenges: levelStateRef.current.totalChallenges,
      lives_remaining: state.lives,
      score_percentage: 100
    })
  }, [track, level, state.lives])

  const trackLevelFailed = useCallback(
    (reason: 'time_over' | 'lives_over') => {
      if (!level || !levelStateRef.current.started) return

      const completionTime = Date.now() - levelStateRef.current.startTime
      const progressPercentage =
        levelStateRef.current.totalChallenges > 0
          ? (levelStateRef.current.challengesCompleted /
              levelStateRef.current.totalChallenges) *
            100
          : 0

      track.event('level_failed', {
        level_id: level.id,
        level_uid: level.uid,
        level_name: level.name,
        failure_reason: reason,
        completion_time: completionTime,
        progress_percentage: progressPercentage,
        challenges_completed: levelStateRef.current.challengesCompleted,
        total_challenges: levelStateRef.current.totalChallenges,
        lives_remaining: reason === 'lives_over' ? 0 : state.lives
      })
    },
    [track, level, state.lives]
  )

  useEffect(() => {
    if (level && !levelStateRef.current.viewed) {
      trackLevelViewed()
    }

    if (
      state.steps &&
      state.currentIndex !== undefined &&
      !levelStateRef.current.started
    ) {
      const currentStep = state.steps[state.currentIndex]
      if (currentStep?.kind === STEP_KIND.CHALLENGE) {
        trackLevelStartedOrResumed('fresh')
      }
    }

    if (state.status === 'time_over') {
      trackLevelFailed('time_over')
    }

    if (state.status === 'lives_over') {
      trackLevelFailed('lives_over')
    }

    if (state.status === 'completed') {
      trackLevelFinished()
    }
  }, [
    state.status,
    state.currentIndex,
    state.steps,
    challenges,
    level,
    trackLevelViewed,
    trackLevelStartedOrResumed,
    trackLevelFailed,
    trackLevelFinished
  ])

  useEffect(() => {
    return () => {
      if (levelStateRef.current.started && level) {
        trackLevelLeave(false)
      }
    }
  }, [trackLevelLeave, level])

  return {
    trackLevelViewed,
    trackLevelStartedOrResumed,
    trackLevelLeave,
    trackLevelFinished,
    trackLevelFailed
  }
}
